import type { EstimateBreakdown, Money, MoneyRange } from "../types/index.js";
import type { LineItemCalculationResult } from "./pricing-calculator.js";
import type { PricingLineItem } from "../types/index.js";
import { roundMoneyRange, DEFAULT_ROUNDING_CONFIG, type RoundingConfig } from "./rounding.js";

/** Categories whose entire cost belongs in the fixtures/appliances bucket rather than being split by labour/material percentages. */
const FIXTURE_APPLIANCE_CATEGORIES = new Set([
  "appliances",
  "vanity",
  "toilet",
  "shower_tub",
  "glass",
  "ventilation",
  "island",
  "egress",
  "freestanding_tub"
]);

/** Categories whose entire cost belongs in the permits/design/misc bucket. */
const PERMIT_DESIGN_CATEGORIES = new Set(["permit_design_allowance"]);

export type BreakdownBucket = keyof EstimateBreakdown;

const ZERO: Money = { amount: 0, currency: "CAD" };
const ZERO_RANGE: MoneyRange = { low: ZERO, typical: ZERO, high: ZERO };

export function emptyBreakdown(): EstimateBreakdown {
  return {
    labour: { ...ZERO_RANGE },
    materials: { ...ZERO_RANGE },
    fixturesAndAppliances: { ...ZERO_RANGE },
    permitsDesignAndMiscAllowance: { ...ZERO_RANGE }
  };
}

function addRange(a: MoneyRange, b: { low: number; typical: number; high: number }): MoneyRange {
  return {
    low: { amount: a.low.amount + b.low, currency: "CAD" },
    typical: { amount: a.typical.amount + b.typical, currency: "CAD" },
    high: { amount: a.high.amount + b.high, currency: "CAD" }
  };
}

/**
 * Classifies one computed line item's cost into the four
 * `EstimateBreakdown` buckets and accumulates it into `breakdown` in
 * place. Fixture/appliance and permit/design categories go entirely into
 * their own bucket; everything else splits between labour and materials
 * using the line item's own `components` percentages (defaulting to an
 * even 50/50 split when a record doesn't specify one), applied
 * consistently across low/typical/high so the range stays internally
 * consistent.
 */
export function accumulateIntoBreakdown(
  breakdown: EstimateBreakdown,
  item: PricingLineItem,
  result: LineItemCalculationResult
): void {
  const low = result.low?.amount ?? 0;
  const typical = result.typical?.amount ?? 0;
  const high = result.high?.amount ?? 0;
  if (low === 0 && typical === 0 && high === 0) return;

  if (PERMIT_DESIGN_CATEGORIES.has(item.category)) {
    breakdown.permitsDesignAndMiscAllowance = addRange(breakdown.permitsDesignAndMiscAllowance, { low, typical, high });
    return;
  }

  if (FIXTURE_APPLIANCE_CATEGORIES.has(item.category)) {
    breakdown.fixturesAndAppliances = addRange(breakdown.fixturesAndAppliances, { low, typical, high });
    return;
  }

  const labourPercent = item.components.labourPercent ?? 50;
  const materialPercent = item.components.materialPercent ?? 100 - labourPercent;
  const totalPercent = labourPercent + materialPercent || 100;
  const labourShare = labourPercent / totalPercent;
  const materialShare = materialPercent / totalPercent;

  breakdown.labour = addRange(breakdown.labour, { low: low * labourShare, typical: typical * labourShare, high: high * labourShare });
  breakdown.materials = addRange(breakdown.materials, {
    low: low * materialShare,
    typical: typical * materialShare,
    high: high * materialShare
  });
}

export function sumBreakdown(breakdown: EstimateBreakdown): MoneyRange {
  const buckets: MoneyRange[] = [
    breakdown.labour,
    breakdown.materials,
    breakdown.fixturesAndAppliances,
    breakdown.permitsDesignAndMiscAllowance
  ];
  return {
    low: { amount: buckets.reduce((s, b) => s + b.low.amount, 0), currency: "CAD" },
    typical: { amount: buckets.reduce((s, b) => s + b.typical.amount, 0), currency: "CAD" },
    high: { amount: buckets.reduce((s, b) => s + b.high.amount, 0), currency: "CAD" }
  };
}

export interface ConsistentBreakdownRounding {
  roundedBreakdown: EstimateBreakdown;
  roundedTotal: MoneyRange;
}

/**
 * Phase 20 audit fix: engine.ts used to round `constructionEstimate` and
 * each of the four breakdown buckets independently, each via its own call
 * to `roundMoneyRange` - since each figure gets rounded to the increment
 * tier for ITS OWN magnitude (see rounding.ts's `tierFor`), a bucket can
 * land in a coarser/finer tier than the total, and the four rounded
 * buckets are not guaranteed to sum to the rounded total. That's a real
 * problem for a "here's your estimate" figure sitting directly above a
 * breakdown that's supposed to add up to it.
 *
 * Fixes this by rounding the true (pre-rounding) total once - that's the
 * authoritative headline figure - then rounding each bucket independently
 * as before, and finally nudging whichever bucket has the largest
 * magnitude by the (small, at most one or two rounding increments)
 * leftover difference so the four rounded buckets always sum exactly to
 * the rounded total, for low/typical/high independently.
 */
export function roundBreakdownConsistently(
  breakdown: EstimateBreakdown,
  trueTotal: MoneyRange,
  config: RoundingConfig = DEFAULT_ROUNDING_CONFIG
): ConsistentBreakdownRounding {
  const roundedTotal = roundMoneyRange(trueTotal, config);

  const buckets: BreakdownBucket[] = ["labour", "materials", "fixturesAndAppliances", "permitsDesignAndMiscAllowance"];
  const roundedBuckets: Record<BreakdownBucket, MoneyRange> = {
    labour: roundMoneyRange(breakdown.labour, config),
    materials: roundMoneyRange(breakdown.materials, config),
    fixturesAndAppliances: roundMoneyRange(breakdown.fixturesAndAppliances, config),
    permitsDesignAndMiscAllowance: roundMoneyRange(breakdown.permitsDesignAndMiscAllowance, config)
  };

  for (const key of ["low", "typical", "high"] as const) {
    const sum = buckets.reduce((s, b) => s + roundedBuckets[b][key].amount, 0);
    const diff = roundedTotal[key].amount - sum;
    if (diff === 0) continue;

    let largestBucket: BreakdownBucket = buckets[0]!;
    for (const b of buckets) {
      if (Math.abs(roundedBuckets[b][key].amount) > Math.abs(roundedBuckets[largestBucket][key].amount)) {
        largestBucket = b;
      }
    }
    roundedBuckets[largestBucket] = {
      ...roundedBuckets[largestBucket],
      [key]: { amount: roundedBuckets[largestBucket][key].amount + diff, currency: "CAD" }
    };
  }

  return {
    roundedBreakdown: {
      labour: roundedBuckets.labour,
      materials: roundedBuckets.materials,
      fixturesAndAppliances: roundedBuckets.fixturesAndAppliances,
      permitsDesignAndMiscAllowance: roundedBuckets.permitsDesignAndMiscAllowance
    },
    roundedTotal
  };
}
